import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Package, Plus, Edit, Trash2, Search, Download, Upload, Archive, ArchiveRestore, Star } from 'lucide-react';
import { SortableHeader, applySorting } from '../components/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const UNITS = ['kg', 'g', 'L', 'ml', 'each', 'tin', 'bottle', 'can', 'bag', 'slice', 'cup', 'portion', 'sheet', 'roll', 'sachet', 'Case', 'Bags', 'Carton', 'Box', 'Pack', 'Bucket', 'Tub', 'Packet', 'Ea', 'Punnet'];

const StockItems = () => {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const handleSort = (field, dir) => { setSortField(field); setSortDirection(dir); };

  const [formData, setFormData] = useState({
    name: '', sku: '', description: '', unit: 'kg',
    recipe_unit: '', recipe_unit_qty: 1,
    suppliers: [], par_values: {}
  });

  useEffect(() => { fetchData(); }, [showArchived]);

  const fetchData = async () => {
    try {
      const params = showArchived ? { include_archived: true } : {};
      const [itemsRes, suppliersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/stock-items`, { withCredentials: true, params }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true })
      ]);
      setItems(itemsRes.data);
      setSuppliers(suppliersRes.data);
      setBranches(branchesRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        const res = await axios.put(`${API}/stock-items/${editingItem.stock_item_id}`, formData, { withCredentials: true });
        toast.success('Stock item updated');
        // Show recipe recalculation info if prices changed
        const recalc = res.data?.recipe_recalculation;
        if (recalc && recalc.affected_count > 0) {
          const msg = `${recalc.affected_count} recipe${recalc.affected_count > 1 ? 's' : ''} recalculated`;
          const detail = [];
          if (recalc.new_alerts > 0) detail.push(`${recalc.new_alerts} new cost alert${recalc.new_alerts > 1 ? 's' : ''}`);
          if (recalc.resolved_alerts > 0) detail.push(`${recalc.resolved_alerts} alert${recalc.resolved_alerts > 1 ? 's' : ''} resolved`);
          toast.info(msg + (detail.length > 0 ? ` (${detail.join(', ')})` : ''), { duration: 6000 });
        }
      } else {
        await axios.post(`${API}/stock-items`, formData, { withCredentials: true });
        toast.success('Stock item created');
      }
      setIsModalOpen(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stock item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', sku: '', description: '', unit: 'kg',
      recipe_unit: '', recipe_unit_qty: 1,
      suppliers: [], par_values: {}
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Build suppliers list with per-supplier config
    let suppliersList = (item.suppliers || []).map(s => ({
      supplier_id: s.supplier_id,
      is_preferred: s.is_preferred || false,
      cost_price: s.cost_price || 0,
      order_unit: s.order_unit || '',
      order_unit_qty: s.order_unit_qty || 1,
      notes: s.notes || ''
    }));
    if (suppliersList.length === 0 && item.supplier_id) {
      suppliersList = [{
        supplier_id: item.supplier_id, is_preferred: true,
        cost_price: item.cost_price || 0,
        order_unit: item.order_unit || '',
        order_unit_qty: item.order_unit_qty || 1,
        notes: item.notes || ''
      }];
    }
    setFormData({
      name: item.name || '', sku: item.sku || '', description: item.description || '',
      unit: item.unit || 'kg', recipe_unit: item.recipe_unit || '',
      recipe_unit_qty: item.recipe_unit_qty || 1,
      suppliers: suppliersList,
      par_values: item.par_values || {}
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this stock item?')) return;
    try {
      await axios.delete(`${API}/stock-items/${itemId}`, { withCredentials: true });
      toast.success('Stock item deleted');
      fetchData();
    } catch { toast.error('Failed to delete stock item'); }
  };

  const handleArchive = async (item) => {
    const isArchived = item.archived;
    const action = isArchived ? 'unarchive' : 'archive';
    try {
      await axios.post(`${API}/stock-items/${item.stock_item_id}/${action}`, {}, { withCredentials: true });
      toast.success(`Stock item ${action}d`);
      fetchData();
    } catch { toast.error(`Failed to ${action} stock item`); }
  };

  const addSupplier = (supplierId) => {
    if (!supplierId || formData.suppliers.some(s => s.supplier_id === supplierId)) return;
    const isFirst = formData.suppliers.length === 0;
    setFormData(prev => ({
      ...prev,
      suppliers: [...prev.suppliers, { supplier_id: supplierId, is_preferred: isFirst, cost_price: 0, order_unit: '', order_unit_qty: 1, notes: '' }]
    }));
  };

  const removeSupplier = (supplierId) => {
    setFormData(prev => {
      const updated = prev.suppliers.filter(s => s.supplier_id !== supplierId);
      if (updated.length > 0 && !updated.some(s => s.is_preferred)) {
        updated[0].is_preferred = true;
      }
      return { ...prev, suppliers: updated };
    });
  };

  const setPreferred = (supplierId) => {
    setFormData(prev => ({
      ...prev,
      suppliers: prev.suppliers.map(s => ({
        ...s, is_preferred: s.supplier_id === supplierId
      }))
    }));
  };

  const updateSupplierField = (supplierId, field, value) => {
    setFormData(prev => ({
      ...prev,
      suppliers: prev.suppliers.map(s =>
        s.supplier_id === supplierId ? { ...s, [field]: value } : s
      )
    }));
  };

  const updateParValue = (branchId, value) => {
    setFormData(prev => ({
      ...prev, par_values: { ...prev.par_values, [branchId]: parseFloat(value) || 0 }
    }));
  };

  const openNewModal = () => { setEditingItem(null); resetForm(); setIsModalOpen(true); };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/stock-items/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stock_items.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Stock items exported');
    } catch { toast.error('Failed to export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/stock-items/import/csv`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSupplier = !filterSupplier || item.supplier_id === filterSupplier ||
                            (item.suppliers || []).some(s => s.supplier_id === filterSupplier);
    return matchesSearch && matchesSupplier;
  });

  const sortedItems = applySorting(filteredItems, sortField, sortDirection);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="stock-items-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Stock Items
          </h1>
          <p className="text-muted-foreground mt-1">Items you buy from suppliers (ingredients, supplies)</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-2 mr-2">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-stock-toggle" />
            <Label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setShowArchived(!showArchived)}>
              Show Archived
            </Label>
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-stock-items-btn">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
          <Button variant="outline" asChild data-testid="import-stock-items-btn">
            <label className="cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />Import
              <input type="file" accept=".csv" onChange={handleImport} className="hidden" />
            </label>
          </Button>
          <Button onClick={openNewModal} data-testid="add-stock-item-btn">
            <Plus className="w-4 h-4 mr-2" />Add Stock Item
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search stock items..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-stock-items-input" />
            </div>
            <Select value={filterSupplier || "all"} onValueChange={(val) => setFilterSupplier(val === "all" ? "" : val)}>
              <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="All Suppliers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      {filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No stock items found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || filterSupplier
                  ? 'Try adjusting your filters'
                  : 'Add stock items (things you buy) to use in recipes'}
              </p>
              {!searchQuery && !filterSupplier && (
                <Button onClick={openNewModal}><Plus className="w-4 h-4 mr-2" />Add Stock Item</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader label="Item" field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead>Suppliers</TableHead>
                  <TableHead><SortableHeader label="Count Unit" field="unit" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead>Order Unit</TableHead>
                  <TableHead><SortableHeader label="Cost" field="cost_price" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead>Order Days</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedItems.map((item) => (
                  <TableRow key={item.stock_item_id} className={item.archived ? 'opacity-50' : ''} data-testid={`stock-item-row-${item.stock_item_id}`}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.name}</p>
                          {item.archived && <Badge variant="outline" className="text-xs bg-muted">Archived</Badge>}
                        </div>
                        {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                        {item.notes && <p className="text-xs text-muted-foreground italic">{item.notes}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {item.suppliers?.length > 0 ? item.suppliers.map((s) => (
                          <div key={s.supplier_id} className="flex items-center gap-1.5 text-sm">
                            <span className={s.is_preferred ? 'font-semibold' : 'text-muted-foreground'}>{s.supplier_name || 'Unknown'}</span>
                            {s.cost_price > 0 && (
                              <span className="text-xs text-muted-foreground">
                                R{s.cost_price.toFixed(2)}{s.order_unit ? `/${s.order_unit}` : ''}
                              </span>
                            )}
                          </div>
                        )) : (
                          <span className="text-sm">{item.supplier_name || 'N/A'}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {item.order_unit || item.unit}
                        {item.order_unit_qty > 1 && (
                          <span className="text-xs text-muted-foreground block">({item.order_unit_qty} {item.unit})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        R{item.cost_price?.toFixed(2)}
                        {item.order_unit_qty > 1 && (
                          <span className="text-xs text-muted-foreground block">
                            R{(item.cost_price / item.order_unit_qty / (item.recipe_unit_qty || 1)).toFixed(2)}/{item.recipe_unit || item.unit}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          // Collect order days from all suppliers
                          const days = new Set();
                          (item.suppliers || []).forEach(s => (s.order_days || []).forEach(d => days.add(d)));
                          // Fallback to item-level order_days for old data
                          if (days.size === 0) (item.order_days || []).forEach(d => days.add(d));
                          const sorted = DAYS_OF_WEEK.filter(d => days.has(d));
                          return sorted.length > 0 ? sorted.map(day => (
                            <Badge key={day} variant="outline" className="text-xs">{day.slice(0, 3)}</Badge>
                          )) : (
                            <span className="text-xs text-muted-foreground">Not set</span>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleArchive(item)}
                          title={item.archived ? 'Unarchive' : 'Archive'}
                          data-testid={`archive-stock-item-${item.stock_item_id}`}>
                          {item.archived
                            ? <ArchiveRestore className="w-4 h-4 text-green-600" />
                            : <Archive className="w-4 h-4 text-amber-600" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.stock_item_id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Stock Item' : 'Add New Stock Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input id="name" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Milk Full Cream" required data-testid="stock-item-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input id="sku" value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="e.g., MLK-001" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit">Count Unit *</Label>
                <div className="relative">
                  <Input
                    id="unit"
                    list="unit-suggestions"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="e.g., Carton"
                    required
                    data-testid="stock-item-unit-select"
                  />
                  <datalist id="unit-suggestions">
                    {[...new Set([...UNITS, ...items.map(i => i.unit).filter(Boolean)])]
                      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                      .map((unit) => <option key={unit} value={unit} />)}
                  </datalist>
                </div>
                <p className="text-xs text-muted-foreground">Unit for stock takes & PAR</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipe_unit">Recipe Unit</Label>
                <div className="relative">
                  <Input
                    id="recipe_unit"
                    list="recipe-unit-suggestions"
                    value={formData.recipe_unit}
                    onChange={(e) => setFormData({ ...formData, recipe_unit: e.target.value })}
                    placeholder={`Same as count`}
                    data-testid="stock-item-recipe-unit-input"
                  />
                  <datalist id="recipe-unit-suggestions">
                    {UNITS.map((u) => <option key={u} value={u} />)}
                  </datalist>
                </div>
                <p className="text-xs text-muted-foreground">Unit used in recipes (e.g., L)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipe_unit_qty">Recipe Units per Count</Label>
                <Input id="recipe_unit_qty" type="number" step="0.01" min="0.01"
                  value={formData.recipe_unit_qty}
                  onChange={(e) => setFormData({ ...formData, recipe_unit_qty: parseFloat(e.target.value) || 1 })}
                  disabled={!formData.recipe_unit}
                  data-testid="stock-item-recipe-unit-qty-input" />
                <p className="text-xs text-muted-foreground">
                  {formData.recipe_unit && formData.recipe_unit_qty > 0
                    ? `1 ${formData.unit} = ${formData.recipe_unit_qty} ${formData.recipe_unit}`
                    : 'Leave blank if same'}
                </p>
              </div>
            </div>

            {/* Suppliers & Pricing */}
            <div className="space-y-2">
              <Label>Suppliers & Pricing *</Label>
              {formData.suppliers.length > 0 && (
                <div className="space-y-3">
                  {formData.suppliers.map((s) => {
                    const sup = suppliers.find(x => x.supplier_id === s.supplier_id);
                    return (
                      <div key={s.supplier_id} className={`p-3 border rounded-lg ${s.is_preferred ? 'border-primary/50 bg-primary/5' : 'bg-muted/30'}`} data-testid={`supplier-row-${s.supplier_id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Button type="button" variant={s.is_preferred ? "default" : "ghost"} size="icon" className="h-7 w-7 flex-shrink-0"
                            onClick={() => setPreferred(s.supplier_id)} title="Set as preferred">
                            <Star className={`w-3.5 h-3.5 ${s.is_preferred ? 'fill-current' : ''}`} />
                          </Button>
                          <span className={`text-sm flex-1 ${s.is_preferred ? 'font-semibold' : ''}`}>{sup?.name || s.supplier_id}</span>
                          {s.is_preferred && <Badge variant="outline" className="text-xs">Preferred</Badge>}
                          {sup?.order_days?.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {sup.order_days.map(d => d.slice(0, 3)).join(', ')}
                            </span>
                          )}
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
                            onClick={() => removeSupplier(s.supplier_id)}>
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Order Unit</Label>
                            <Input className="h-8 text-sm" value={s.order_unit || ''}
                              onChange={(e) => updateSupplierField(s.supplier_id, 'order_unit', e.target.value)}
                              placeholder={`e.g., Case`} data-testid={`supplier-order-unit-${s.supplier_id}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">{formData.unit}s per Order</Label>
                            <Input className="h-8 text-sm" type="number" step="0.01" min="0.01"
                              value={s.order_unit_qty || ''}
                              onChange={(e) => updateSupplierField(s.supplier_id, 'order_unit_qty', parseFloat(e.target.value) || 1)}
                              placeholder="1" data-testid={`supplier-order-qty-${s.supplier_id}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Cost per Order Unit (R)</Label>
                            <Input className="h-8 text-sm" type="number" step="0.01" min="0"
                              value={s.cost_price || ''}
                              onChange={(e) => updateSupplierField(s.supplier_id, 'cost_price', parseFloat(e.target.value) || 0)}
                              placeholder="0.00" data-testid={`supplier-cost-${s.supplier_id}`} />
                          </div>
                        </div>
                        {s.cost_price > 0 && (s.order_unit_qty > 1 || formData.recipe_unit) && (
                          <div className="mt-1.5 text-xs text-muted-foreground">
                            {s.order_unit_qty > 1 && <span>R{(s.cost_price / s.order_unit_qty).toFixed(2)}/{formData.unit}</span>}
                            {formData.recipe_unit && (
                              <span>{s.order_unit_qty > 1 ? ' · ' : ''}R{(s.cost_price / (s.order_unit_qty || 1) / (formData.recipe_unit_qty || 1)).toFixed(2)}/{formData.recipe_unit}</span>
                            )}
                          </div>
                        )}
                        <div className="mt-2">
                          <Input className="h-8 text-sm" value={s.notes || ''}
                            onChange={(e) => updateSupplierField(s.supplier_id, 'notes', e.target.value)}
                            placeholder="Notes (e.g., minimum order, delivery info)"
                            data-testid={`supplier-notes-${s.supplier_id}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Select value="pick" onValueChange={(v) => v !== 'pick' && addSupplier(v)}>
                <SelectTrigger data-testid="add-supplier-select">
                  <SelectValue placeholder="Add supplier..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pick" disabled>Add supplier...</SelectItem>
                  {suppliers.filter(s => !formData.suppliers.some(fs => fs.supplier_id === s.supplier_id))
                    .map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {formData.suppliers.length === 0 && (
                <p className="text-xs text-muted-foreground">Add at least one supplier. Star = preferred (used for recipe costing).</p>
              )}
            </div>

            {branches.length > 0 && (
              <div className="space-y-2">
                <Label>PAR Values by Branch <span className="text-muted-foreground font-normal">({formData.unit})</span></Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {branches.map((branch) => (
                    <div key={branch.branch_id} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{branch.name}</Label>
                      <div className="flex items-center gap-1.5">
                        <Input type="number" step="0.01" min="0"
                          value={formData.par_values[branch.branch_id] || ''}
                          onChange={(e) => updateParValue(branch.branch_id, e.target.value)}
                          placeholder="0" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{formData.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-stock-item-btn">
                {editingItem ? 'Update' : 'Create'} Stock Item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockItems;
